import { Button, Flex, Text, Tooltip } from "@mantine/core";
import { useClickOutside } from "@mantine/hooks";
import { PropsWithChildren, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

interface ConfirmationPopoverProps {
  cancelButtonContent?: ReactNode;
  isDanger?: boolean;
  isOpen: boolean;
  message: ReactNode;
  onCancelButtonClick?: () => void;
  onClose: () => void;
  onPrimaryButtonClick: () => void;
  primaryButtonContent: ReactNode;
  wide?: boolean;
}

export const ConfirmationPopover = ({
  cancelButtonContent = "Cancel",
  children,
  isDanger = false,
  isOpen,
  message,
  onCancelButtonClick,
  onClose,
  onPrimaryButtonClick,
  primaryButtonContent,
  wide = false,
}: PropsWithChildren<ConfirmationPopoverProps>) => {
  const ref = useClickOutside(onClose);

  return (
    <Tooltip
      arrowSize={18}
      classNames={{
        tooltip: twMerge([
          "bg-white",
          "drop-shadow-[0px_0px_24px_rgba(0,0,0,0.10)]",
          "p-0",
          "pointer-events-auto",
          wide ? "w-[436px]" : "w-[336px]",
        ]),
      }}
      label={
        <Flex
          className="p-[20px_6px_16px_30px]"
          direction="column"
          ref={ref}
          data-confirmation-popover="true"
        >
          <Text className="max-w-full text-sm text-uw-gray-9 text-wrap">
            {message}
          </Text>
          <Flex className="mt-4">
            <Button
              className={twMerge([
                "mr-2.5",
                isDanger && ["bg-uw-red", "text-white"],
              ])}
              onClick={() => {
                onPrimaryButtonClick();
                onClose();
              }}
            >
              {primaryButtonContent}
            </Button>
            <Button
              className="bg-uw-gray-0 text-uw-gray-9"
              onClick={() => {
                onCancelButtonClick?.();
                onClose();
              }}
            >
              {cancelButtonContent}
            </Button>
          </Flex>
        </Flex>
      }
      opened={isOpen}
      withArrow
    >
      {children}
    </Tooltip>
  );
};
